/*
 * Lets users safely replace a Box folder's exclusion lists without background
 * polling overwriting edits that are already in progress.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useUpdateSyncTargetMutation } from 'src/store/bender-api';
import { type SyncTargetDto } from 'src/types/sync-target.types';
import { normalizeBenderError } from 'src/utils/benderErrors';
import {
  ExclusionValidationMessages,
  MAX_EXCLUDED_ENTRIES,
  validateFolderIdEntry,
  validatePathnameEntry,
} from 'src/utils/exclusionValidation';
import { Logger } from 'src/utils/logger';

interface EditBoxFolderExclusionsModalProps {
  open: boolean;
  syncTarget: SyncTargetDto;
  onClose: () => void;
}

interface ExclusionListEditorProps {
  addLabel: string;
  input: string;
  inputHelperText: string;
  inputLabel: string;
  inputPlaceholder: string;
  isDisabled: boolean;
  testIdPrefix: string;
  validationError: string | null;
  values: readonly string[];
  onAdd: () => void;
  onInputChange: (value: string) => void;
  onRemove: (index: number) => void;
}

type ValidationDiscriminant = typeof ExclusionValidationMessages[keyof typeof ExclusionValidationMessages];

/** Leaves enough horizontal room for path patterns after the Add button takes its fitted width. */
const MODAL_WIDTH = 640;

const VALIDATION_MESSAGE_KEYS: Record<ValidationDiscriminant, string> = {
  [ExclusionValidationMessages.InvalidFolderId]: 'box_folder_exclusions_invalid_folder_id',
  [ExclusionValidationMessages.EmptyPathname]: 'box_folder_exclusions_empty_pattern',
  [ExclusionValidationMessages.PathnameTooLong]: 'box_folder_exclusions_pattern_too_long',
  [ExclusionValidationMessages.Duplicate]: 'box_folder_exclusions_duplicate',
  [ExclusionValidationMessages.ListFull]: 'box_folder_exclusions_list_full',
};

const getValidationMessage = (t: TranslateFn, validation: string): string => {
  const messageKey = VALIDATION_MESSAGE_KEYS[validation as ValidationDiscriminant];
  return messageKey === undefined
    ? t('box_folder_exclusions_invalid_pattern', { error: validation })
    : t(messageKey);
};

const ExclusionListEditor: React.FC<ExclusionListEditorProps> = ({
  addLabel,
  input,
  inputHelperText,
  inputLabel,
  inputPlaceholder,
  isDisabled,
  testIdPrefix,
  validationError,
  values,
  onAdd,
  onInputChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const isAtLimit = values.length >= MAX_EXCLUDED_ENTRIES;

  return (
    <Column data-testid={`${testIdPrefix}-editor`} spacing="200">
      <Row alignmentVertical="bottom" spacing="300" widths={['fill', 'fit']}>
        <Input
          data-testid={`${testIdPrefix}-input`}
          disabled={isDisabled}
          error={validationError !== null}
          errorMessage={validationError ?? undefined}
          helperText={inputHelperText}
          label={inputLabel}
          placeholder={inputPlaceholder}
          value={input}
          onChange={onInputChange}
        />
        <Button
          data-testid={`${testIdPrefix}-add-button`}
          disabled={isDisabled || isAtLimit}
          type="secondary"
          onClick={onAdd}
        >
          {addLabel}
        </Button>
      </Row>

      {values.map((entry, index) => (
        <Row
          alignmentVertical="center"
          key={`${entry}-${index}`}
          spacing="200"
          widths={['fill', 'fit']}
        >
          <Column minWidth={0}>
            <Text
              breakWord={true}
              data-testid={`${testIdPrefix}-entry-${index}`}
              tag="div"
            >
              {entry}
            </Text>
          </Column>
          <Button
            aria-label={t('box_folder_exclusions_remove_entry', { entry })}
            data-testid={`${testIdPrefix}-remove-${index}`}
            disabled={isDisabled}
            size="small"
            type="tertiary"
            onClick={() => { onRemove(index); }}
          >
            {t('box_folder_exclusions_remove')}
          </Button>
        </Row>
      ))}
    </Column>
  );
};

interface OpenEditorProps {
  syncTarget: SyncTargetDto;
  onClose: () => void;
}

const OpenEditor: React.FC<OpenEditorProps> = ({ syncTarget, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [updateSyncTarget, { isLoading: isSaving }] = useUpdateSyncTargetMutation();

  const [folderIds, setFolderIds] = useState(() => [...(syncTarget.excludedFolderIds ?? [])]);
  const [pathnames, setPathnames] = useState(() => [...(syncTarget.excludedPathnames ?? [])]);
  const [folderIdInput, setFolderIdInput] = useState('');
  const [pathnameInput, setPathnameInput] = useState('');
  const [folderIdError, setFolderIdError] = useState<string | null>(null);
  const [pathnameError, setPathnameError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<'missing' | 'retryable' | null>(null);

  const addFolderId = () => {
    const validation = validateFolderIdEntry(folderIdInput, folderIds);
    if (validation !== null) {
      setFolderIdError(getValidationMessage(t, validation));
      return;
    }
    setFolderIds([...folderIds, folderIdInput]);
    setFolderIdInput('');
    setFolderIdError(null);
  };

  const addPathname = () => {
    const validation = validatePathnameEntry(pathnameInput, pathnames);
    if (validation !== null) {
      setPathnameError(getValidationMessage(t, validation));
      return;
    }
    setPathnames([...pathnames, pathnameInput]);
    setPathnameInput('');
    setPathnameError(null);
  };

  const close = () => {
    if (isSaving) return;
    onClose();
  };

  const save = async () => {
    if (isSaving || saveError === 'missing') return;
    setSaveError(null);

    try {
      await updateSyncTarget({
        syncTargetId: syncTarget.syncTargetId,
        excludedFolderIds: folderIds,
        excludedPathnames: pathnames,
      }).unwrap();
      createToast({
        type: AlertType.SUCCESS,
        message: t('box_folder_exclusions_saved_next_scan'),
      });
      onClose();
    } catch (error) {
      const normalized = normalizeBenderError(error);
      Logger.error(`Failed to update exclusions for ${syncTarget.syncTargetId}`, {
        status: normalized.status,
        detail: normalized.message,
      });
      setSaveError(normalized.status === 404 ? 'missing' : 'retryable');
    }
  };

  const isMissing = saveError === 'missing';
  const isEditorDisabled = isSaving;

  return (
    <Modal
      data-testid="edit-box-folder-exclusions-modal"
      open
      title={t('box_folder_exclusions_edit_title', { folderId: syncTarget.targetId })}
      width={MODAL_WIDTH}
      onClose={close}
    >
      <Column spacing="400">
        {saveError !== null && (
          <Alert
            data-testid="edit-box-folder-exclusions-alert"
            size="small"
            type={isMissing ? 'warning' : 'error'}
          >
            {t(isMissing
              ? 'box_folder_exclusions_no_longer_enrolled'
              : 'box_folder_exclusions_save_retry')}
          </Alert>
        )}

        <ExclusionListEditor
          addLabel={t('box_folder_exclusions_add_folder')}
          input={folderIdInput}
          inputHelperText={t('box_folder_exclusions_folder_id_helper')}
          inputLabel={t('box_folder_exclusions_folder_ids_label')}
          inputPlaceholder={t('box_folder_exclusions_folder_id_placeholder')}
          isDisabled={isEditorDisabled}
          testIdPrefix="edit-box-folder-folder-ids"
          validationError={folderIdError}
          values={folderIds}
          onAdd={addFolderId}
          onInputChange={(value) => {
            setFolderIdInput(value);
            setFolderIdError(null);
          }}
          onRemove={(index) => { setFolderIds(folderIds.filter((_, itemIndex) => itemIndex !== index)); }}
        />

        <ExclusionListEditor
          addLabel={t('box_folder_exclusions_add_pattern')}
          input={pathnameInput}
          inputHelperText={t('box_folder_exclusions_pathname_helper')}
          inputLabel={t('box_folder_exclusions_pathnames_label')}
          inputPlaceholder={t('box_folder_exclusions_pathname_placeholder')}
          isDisabled={isEditorDisabled}
          testIdPrefix="edit-box-folder-pathnames"
          validationError={pathnameError}
          values={pathnames}
          onAdd={addPathname}
          onInputChange={(value) => {
            setPathnameInput(value);
            setPathnameError(null);
          }}
          onRemove={(index) => { setPathnames(pathnames.filter((_, itemIndex) => itemIndex !== index)); }}
        />
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200">
          <Button
            data-testid="edit-box-folder-exclusions-cancel-button"
            disabled={isSaving}
            type="tertiary"
            onClick={close}
          >
            {t('form_global_cancel')}
          </Button>
          <Button
            data-testid="edit-box-folder-exclusions-save-button"
            disabled={isSaving || isMissing}
            type="primary"
            onClick={() => { void save(); }}
          >
            {isSaving
              ? <Loader data-testid="edit-box-folder-exclusions-saving" size="small" />
              : t('form_global_save')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

/**
 * Mounting the stateful editor only while open snapshots the selected row once.
 * Polling may replace `syncTarget` while mounted, but useState does not reseed.
 */
export const EditBoxFolderExclusionsModal: React.FC<EditBoxFolderExclusionsModalProps> = (props) => {
  if (!props.open) return null;
  return <OpenEditor syncTarget={props.syncTarget} onClose={props.onClose} />;
};
